/**
 * InvoiceDropdowns — manages the "Service Type" dropdown items used by the
 * invoice creation form.  Bilingual (AR + EN), per-platform (or shared "all"),
 * with add / archive / restore / delete + insertion timestamp.
 *
 * Reads/writes: /api/admin/dropdown-lists  (list_type = "invoice_service_type")
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Plus, Loader2, RefreshCw, Trash2, Archive, ArchiveRestore, Save, X,
} from 'lucide-react';

import { useLanguage } from '../../context/LanguageContext';
import DashboardHeader from '../../components/DashboardHeader';
import BreadcrumbNav from '../../components/BreadcrumbNav';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { getAuthHeaders } from '../../utils/authHeaders';
import { FINANCE_PLATFORM_META } from '../../constants/financePlatforms';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LIST_TYPE = 'invoice_service_type';

// "all" scope means shared across every platform
const PLATFORM_OPTIONS = [
  { id: 'all',                ar: 'مشترك (كل المنصات)', en: 'Shared (all platforms)' },
  ...Object.entries(FINANCE_PLATFORM_META).map(([id, m]) => ({ id, ar: m.ar, en: m.en })),
];

export default function InvoiceDropdowns() {
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const t = (ar, en) => (isAr ? ar : en);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterPlatform, setFilterPlatform] = useState('all');
  const [showArchived, setShowArchived] = useState(false);

  const [creating, setCreating] = useState(false);
  const [newDraft, setNewDraft] = useState({ platform: 'all', label_ar: '', label_en: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { list_type: LIST_TYPE, include_archived: showArchived };
      const r = await axios.get(`${API_URL}/api/admin/dropdown-lists`,
        { headers: getAuthHeaders(), params });
      setItems(r.data.data || []);
    } catch {
      toast.error(t('فشل التحميل', 'Load failed'));
    } finally {
      setLoading(false);
    }
  }, [showArchived, isAr]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (filterPlatform === 'all') return items;
    return items.filter((it) => it.platform === filterPlatform || it.platform === 'all');
  }, [items, filterPlatform]);

  const create = async () => {
    if (!newDraft.label_ar.trim() || !newDraft.label_en.trim()) {
      toast.error(t('الرجاء إدخال الاسم بالعربي والإنجليزي', 'Please enter both AR and EN labels'));
      return;
    }
    setCreating(true);
    try {
      await axios.post(`${API_URL}/api/admin/dropdown-lists`, {
        list_type: LIST_TYPE,
        platform: newDraft.platform,
        label_ar: newDraft.label_ar.trim(),
        label_en: newDraft.label_en.trim(),
        sort_order: (items[items.length - 1]?.sort_order || 100) + 10,
      }, { headers: getAuthHeaders() });
      toast.success(t('✓ تمت الإضافة', '✓ Added'));
      setNewDraft({ platform: newDraft.platform, label_ar: '', label_en: '' });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('فشل الإضافة', 'Add failed'));
    } finally {
      setCreating(false);
    }
  };

  const toggleArchive = async (it) => {
    try {
      await axios.post(`${API_URL}/api/admin/dropdown-lists/${it.id}/archive`, {},
        { headers: getAuthHeaders() });
      toast.success(it.archived ? t('تمت الاستعادة', 'Restored') : t('تمت الأرشفة', 'Archived'));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('فشل التحديث', 'Update failed'));
    }
  };

  const remove = async (it) => {
    if (!window.confirm(t('حذف نهائي لهذا العنصر؟', 'Permanently delete this item?'))) return;
    try {
      await axios.delete(`${API_URL}/api/admin/dropdown-lists/${it.id}`,
        { headers: getAuthHeaders() });
      toast.success(t('تم الحذف', 'Deleted'));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('فشل الحذف', 'Delete failed'));
    }
  };

  return (
    <div className="min-h-screen bg-[#030303] relative">
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-[#d4af37]/5 rounded-full blur-[150px]" />
      </div>

      <DashboardHeader showBackButton backPath="/dashboard/finance/settings/dropdown-lists" />

      <div className="border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <BreadcrumbNav items={[
            { label_ar: 'لوحة التحكم', label_en: 'Dashboard', path: '/dashboard' },
            { label_ar: 'المالية', label_en: 'Finance', path: '/dashboard/finance' },
            { label_ar: 'إعدادات القسم المالي', label_en: 'Finance Settings', path: '/dashboard/finance/settings' },
            { label_ar: 'القوائم المنسدلة', label_en: 'Dropdown Lists', path: '/dashboard/finance/settings/dropdown-lists' },
            { label_ar: 'قوائم إنشاء الفاتورة', label_en: 'Invoice Creation Dropdowns', path: null },
          ]} />
        </div>
      </div>

      <main className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className={`mb-8 ${isAr ? 'text-right' : 'text-left'}`}>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
            {t('قوائم إنشاء الفاتورة', 'Invoice Creation Dropdowns')}
          </h1>
          <p className="text-white/50 text-sm max-w-2xl">
            {t('إدارة أنواع الخدمة (Service Type) التي تظهر في نموذج إنشاء الفاتورة، لكل منصة أو مشترك بين الجميع.',
               'Manage the Service Types shown in the invoice form. Per-platform or shared across all platforms.')}
          </p>
        </div>

        {/* Add new */}
        <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-5 mb-6"
             data-testid="dropdown-add-panel">
          <h3 className="text-white font-bold mb-3 text-sm">{t('إضافة عنصر جديد', 'Add new item')}</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <label className="block text-white/60 text-[11px] mb-1">{t('المنصة', 'Platform')}</label>
              <select
                value={newDraft.platform}
                onChange={(e) => setNewDraft(d => ({ ...d, platform: e.target.value }))}
                className="w-full h-10 rounded-md bg-[#050505] border border-white/10 text-white px-3 text-sm"
                data-testid="dropdown-new-platform"
              >
                {PLATFORM_OPTIONS.map(p => (
                  <option key={p.id} value={p.id}>{isAr ? p.ar : p.en}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-white/60 text-[11px] mb-1">{t('الاسم بالعربي', 'Arabic label')}</label>
              <Input
                value={newDraft.label_ar}
                onChange={(e) => setNewDraft(d => ({ ...d, label_ar: e.target.value }))}
                placeholder={t('تسجيل رخصة تجارية', 'e.g. تسجيل رخصة تجارية')}
                dir="rtl"
                className="bg-[#050505] border-white/10 text-white h-10"
                data-testid="dropdown-new-label-ar"
              />
            </div>
            <div>
              <label className="block text-white/60 text-[11px] mb-1">{t('الاسم بالإنجليزي', 'English label')}</label>
              <Input
                value={newDraft.label_en}
                onChange={(e) => setNewDraft(d => ({ ...d, label_en: e.target.value }))}
                placeholder="Trade License Registration"
                dir="ltr"
                className="bg-[#050505] border-white/10 text-white h-10"
                data-testid="dropdown-new-label-en"
              />
            </div>
            <div className="flex items-end">
              <Button
                onClick={create}
                disabled={creating}
                className="w-full bg-[#d4af37] hover:bg-[#d4af37]/90 text-black font-bold h-10"
                data-testid="dropdown-new-submit"
              >
                {creating
                  ? <Loader2 className="w-4 h-4 animate-spin" />
                  : <><Plus className="w-4 h-4 me-1" />{t('إضافة', 'Add')}</>}
              </Button>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <select
            value={filterPlatform}
            onChange={(e) => setFilterPlatform(e.target.value)}
            className="h-9 rounded-md bg-[#050505] border border-white/10 text-white px-3 text-sm"
            data-testid="dropdown-filter-platform"
          >
            <option value="all">{t('كل المنصات', 'All platforms')}</option>
            {PLATFORM_OPTIONS.filter(p => p.id !== 'all').map(p => (
              <option key={p.id} value={p.id}>{isAr ? p.ar : p.en}</option>
            ))}
          </select>
          <label className="inline-flex items-center gap-2 text-white/60 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
              className="accent-[#d4af37]"
              data-testid="dropdown-show-archived"
            />
            {t('إظهار المؤرشفة', 'Show archived')}
          </label>
          <div className="flex-1" />
          <Button onClick={load} variant="outline" size="sm"
                  className="border-white/10 bg-transparent text-white/80 hover:bg-white/5"
                  data-testid="dropdown-refresh">
            <RefreshCw className={`w-4 h-4 me-1 ${loading ? 'animate-spin' : ''}`} />
            {t('تحديث', 'Refresh')}
          </Button>
        </div>

        {/* Items table */}
        <div className="bg-white/[0.02] border border-white/10 rounded-2xl overflow-hidden">
          {loading ? (
            <div className="p-16 flex justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-white/40" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center text-white/40 text-sm">
              {t('لا توجد عناصر بعد. أضِف أول عنصر من الأعلى.',
                'No items yet. Add your first item above.')}
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-white/50 text-xs border-b border-white/5">
                  <th className="text-start p-3 font-medium">{t('المنصة', 'Platform')}</th>
                  <th className="text-start p-3 font-medium">{t('الاسم عربي', 'Arabic')}</th>
                  <th className="text-start p-3 font-medium">{t('الاسم إنجليزي', 'English')}</th>
                  <th className="text-start p-3 font-medium">{t('تاريخ الإضافة', 'Added')}</th>
                  <th className="text-start p-3 font-medium">{t('الحالة', 'Status')}</th>
                  <th className="text-end p-3 font-medium">{t('إجراءات', 'Actions')}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((it) => {
                  const platMeta = PLATFORM_OPTIONS.find(p => p.id === it.platform);
                  return (
                    <tr key={it.id}
                        className={`border-b border-white/5 hover:bg-white/[0.02] ${it.archived ? 'opacity-50' : ''}`}
                        data-testid={`dropdown-row-${it.id}`}>
                      <td className="p-3 text-white/80">{platMeta ? (isAr ? platMeta.ar : platMeta.en) : it.platform}</td>
                      <td className="p-3 text-white" dir="rtl">{it.label_ar}</td>
                      <td className="p-3 text-white" dir="ltr">{it.label_en}</td>
                      <td className="p-3 text-white/60 text-xs">
                        {new Date(it.created_at).toLocaleString(isAr ? 'ar-EG' : 'en-GB', {
                          year: '2-digit', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit'
                        })}
                      </td>
                      <td className="p-3">
                        {it.archived ? (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-300">
                            {t('مؤرشف', 'Archived')}
                          </span>
                        ) : (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-300">
                            {t('نشط', 'Active')}
                          </span>
                        )}
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-1 justify-end">
                          <button onClick={() => toggleArchive(it)}
                                  className="p-1.5 rounded-md hover:bg-white/5 text-white/60"
                                  title={it.archived ? t('استعادة', 'Restore') : t('أرشفة', 'Archive')}
                                  data-testid={`dropdown-archive-${it.id}`}>
                            {it.archived ? <ArchiveRestore className="w-4 h-4" /> : <Archive className="w-4 h-4" />}
                          </button>
                          <button onClick={() => remove(it)}
                                  className="p-1.5 rounded-md hover:bg-red-500/10 text-red-400/60 hover:text-red-400"
                                  title={t('حذف', 'Delete')}
                                  data-testid={`dropdown-delete-${it.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </main>
    </div>
  );
}
